import type { ApolloClient } from '@apollo/client';
import type { GetUrlMetadataQuery, GetUrlMetadataQueryVariables } from '@/graphql/query/GetUrlMetadata.query';

import { GetUrlMetadataDocument } from '@/graphql/query/GetUrlMetadata.query';

import { OnboardingStatus } from '@/enums/onboarding-status';
import { Domain } from '@/routing/helpers/subdomain-helper';
import { ObjectType } from '@/@types/graphql-types';

import { getPageType } from '@/helpers/tracking/tracking-helper';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';

// prettier-ignore
const GLOBAL_ROUTE_NAMES = [
  'app.home',
  // handled with URL Metadata query from BE
  // 'app.sports.subdomain.landing',
  // 'app.editorial.subdomain.landing',
]

const REFRESH_TOKEN_ROUTE_NAME = 'app.refreshtoken';

export default defineNuxtRouteMiddleware(async (to, from) => {
	if (to.name === REFRESH_TOKEN_ROUTE_NAME || from.name === REFRESH_TOKEN_ROUTE_NAME) return;
	// Skip non-app navigations (assets, source maps, etc.)
	if (to.path.includes('.')) return;

	const nuxtApp = useNuxtApp();
	const routingStore = useRoutingStore();
	const trackingStore = useTrackingStore();

	let urlMetadata = null;
	trackingStore.increasePagesVisited();

	if (to.name && !GLOBAL_ROUTE_NAMES.includes(to.name.toString())) {
		const apolloClient = nuxtApp.$apolloClients.default ?? null;

		urlMetadata = await fetchUrlMetadata(to.path, routingStore.activeDomain, apolloClient);
	} else {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		urlMetadata = fetchMockMetadata(userStore.onboardingStatus, languageStore.locale, routingStore.activeDomain);
	}

	routingStore.setUrlMetadata(urlMetadata);

	const pageType = getPageType(urlMetadata);
	routingStore.pageType = pageType;

	const event = useRequestEvent();
	if (event) {
		event.context.activeRouteName = to?.name ?? null;
		event.context.activePageType = routingStore.pageType ?? null;

		useResponseHeader('x-page-type').value = pageType;
	}
});

async function fetchUrlMetadata(fullPath: string, site: Domain, apolloClient: ApolloClient | null = null) {
	if (apolloClient == null) return null;

	const urlMetadata = await apolloClient.query<GetUrlMetadataQuery, GetUrlMetadataQueryVariables>({
		query: GetUrlMetadataDocument,
		variables: { fullPath, site },
	});

	return urlMetadata.data?.urlV2 ?? null;
}

function fetchMockMetadata(onboardingStatus: OnboardingStatus | null, locale: string, site: Domain) {
	const technicalName =
		site === Domain.EDITORIAL
			? 'VIEW_HOME_GUIDES'
			: onboardingStatus === OnboardingStatus.GLOBAL
			? 'VIEW_HOME_GLOBAL'
			: 'VIEW_HOME';

	return {
		id: 'home',
		urlID: 0,
		locale,
		site,
		objectType: ObjectType.Page,
		fullPath: '/',
		metaTitle: 'JustWatch - The Streaming Guide',
		metaDescription: 'All your streaming services in one app.',
		metaKeywords: '',
		page: { id: 'home', technicalName },
		heading1: null,
		heading2: null,
		metaRobots: '',
		hrefLangTags: [],
		allHrefLangTags: [],
	} as GetUrlMetadataQuery['urlV2'];
}
